import React, { memo } from 'react';
import { Pressable, ScrollView, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { Review } from '@/data/types';
import { colors, radius, spacing, type, useAppTheme } from '@/theme';
import { Avatar } from '../ui/Avatar';
import { RatingStars } from '../ui/RatingStars';
import { formatFriendlyDate } from '@/utils/date';
import { ScaledSheet } from '@/theme/responsive';
import { trText } from '@/i18n/plain';
import { useLanguage } from '@/i18n';

interface Props {
  review: Review;
  showShopName?: boolean;
  /** Shows a "Report" link (objectionable reviews go to the SalunBD team). */
  onReport?: (review: Review) => void;
}

function ReviewCardComponent({ review, showShopName, onReport }: Props) {
  useLanguage(); // redraw when the language changes (trText is read at call time)
  const { colors } = useAppTheme();
  return (
    <View style={[styles.card, { backgroundColor: colors.surface, borderColor: colors.border }]}>
      <View style={styles.header}>
        <Avatar uri={review.customerAvatar} name={review.customerName} size={44} />
        <View style={styles.headerInfo}>
          <Text style={[type.titleLg, { color: colors.textPrimary }]} numberOfLines={1}>{review.customerName}</Text>
          {showShopName && <Text style={[type.caption, styles.shopName, { color: colors.textSecondary }]} numberOfLines={1}>{review.shopName} · {review.stylistName}</Text>}
          <Text style={[type.caption, styles.date, { color: colors.textTertiary }]}>{formatFriendlyDate(review.date)}</Text>
        </View>
        <RatingStars rating={review.overallRating} starsOnly size={12} />
      </View>
      {!!review.comment && <Text style={[type.body, styles.comment, { color: colors.textPrimary }]}>{review.comment}</Text>}
      {!!review.replyText && (
        <View style={[styles.reply, { backgroundColor: colors.surfaceMuted }]}>
          <Text style={[type.caption, { color: colors.textTertiary }]}>
            {trText('Reply from', 'উত্তর দিয়েছেন')} {review.shopName || review.stylistName || trText('the shop', 'শপ')}
            {review.replyAt ? ` · ${formatFriendlyDate(review.replyAt)}` : ''}
          </Text>
          <Text style={[type.bodySm, { color: colors.textPrimary }]}>{review.replyText}</Text>
        </View>
      )}
      {review.tags.length > 0 && (
        <View style={styles.tags}>
          {review.tags.map((tag) => (
            <View key={tag} style={[styles.tag, { backgroundColor: colors.surfaceMuted }]}>
              <Text style={[styles.tagText, { color: colors.textSecondary }]}>{tag}</Text>
            </View>
          ))}
        </View>
      )}
      {onReport ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Report this review"
          onPress={() => onReport(review)}
          hitSlop={8}
          style={styles.report}
        >
          <Ionicons name="flag-outline" size={13} color={colors.textTertiary} />
          <Text style={[type.caption, { color: colors.textTertiary }]}>{trText('Report', 'রিপোর্ট')}</Text>
        </Pressable>
      ) : null}
      {review.photos.length > 0 && (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.photos}>
          {review.photos.map((photo) => (
            <Image
              key={photo}
              source={{ uri: photo }}
              style={[styles.photo, { backgroundColor: colors.surfaceMuted }]}
              contentFit="cover"
              transition={150}
            />
          ))}
        </ScrollView>
      )}
    </View>
  );
}

export const ReviewCard = memo(ReviewCardComponent);

const styles = ScaledSheet.create({
  card: { backgroundColor: colors.surface, borderRadius: radius.lg, borderWidth: 1, borderColor: colors.border, padding: spacing.lg, gap: spacing.sm },
  header: { flexDirection: 'row', gap: spacing.sm, alignItems: 'flex-start' },
  headerInfo: { flex: 1, gap: 1 },
  shopName: { color: colors.textSecondary },
  date: { color: colors.textTertiary },
  comment: { color: colors.textPrimary },
  tags: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
  tag: { backgroundColor: colors.surfaceMuted, paddingHorizontal: spacing.sm, paddingVertical: 4, borderRadius: radius.pill },
  tagText: { fontSize: 11, fontWeight: '600', color: colors.textSecondary },
  photos: { gap: spacing.sm },
  photo: { width: 72, height: 72, borderRadius: radius.sm, backgroundColor: colors.surfaceMuted },
  reply: { borderRadius: radius.md, padding: spacing.sm, gap: 2 },
  report: { flexDirection: 'row', alignItems: 'center', gap: 4, alignSelf: 'flex-start', maxWidth: '100%' },
});
